import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.min.css";

const MyOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [pagination, setPagination] = useState({});
    const [page, setPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedDate, setSelectedDate] = useState('')
    const [debouncedSearch, setDebouncedSearch] = useState(searchTerm);

    const renderPagination = () => {
        if (!pagination.last_page) return null;

        const pages = [];
        const current = pagination.current_page;
        const last = pagination.last_page;

        const delta = 1; // how many pages around current
        let start = Math.max(2, current - delta);
        let end = Math.min(last - 1, current + delta);

        // Always show page 1
        pages.push(1);

        // Left dots
        if (start > 2) {
            pages.push('...');
        }

        // Middle pages
        for (let i = start; i <= end; i++) {
            pages.push(i);
        }

        // Right dots
        if (end < last - 1) {
            pages.push('...');
        }

        // Always show last page
        if (last > 1) {
            pages.push(last);
        }

        return (
            <nav className="mt-4">
                <ul className="pagination justify-content-center">
                    {/* Prev */}
                    <li className={`page-item ${current === 1 ? 'disabled' : ''}`}>
                        <button
                            className="page-link !text-black "
                            onClick={() => fetchOrders(current - 1)}
                        >
                            Previous
                        </button>
                    </li>

                    {pages.map((p, index) =>
                        p === '...' ? (
                            <li key={index} className="page-item disabled">
                                <span className="page-link">…</span>
                            </li>
                        ) : (
                            <li key={index} className="page-item">
                                <button
                                    className={`page-link ${
                                        p === current ? '!bg-purple-500 !text-white border-0' : '!text-black hover:!bg-purple-500 hover:!text-white'
                                    }`}
                                    onClick={() => fetchOrders(p)}
                                >
                                    {p}
                                </button>
                            </li>
                        )
                    )}

                    {/* Next */}
                    <li className={`page-item  ${current === last ? 'disabled' : ''}`}>
                        <button
                            className="page-link !text-black"
                            onClick={() => fetchOrders(current + 1)}
                        >
                            Next
                        </button>
                    </li>
                </ul>
            </nav>
        );
    };

    useEffect(() => {
        const timeout = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 800); // ✅ delay

        return () => clearTimeout(timeout);
    }, [searchTerm]);

    useEffect(() => {
        fetchOrders(1);
    }, [debouncedSearch, selectedDate]);

    const fetchOrders = async (pageNumber = 1) => {
        try {
            setLoading(true);

            const params = new URLSearchParams();
            params.append('page', pageNumber);

            if (debouncedSearch) params.append('search', debouncedSearch);
            if (selectedDate) params.append('date', selectedDate);

            const response = await api.get(`/orders?${params.toString()}`);

            setOrders(response.data.data.data || []);
            setPagination(response.data.data);
            setPage(pageNumber);

        } catch (error) {
            toast.error('Failed to load orders');
        } finally {
            setLoading(false);
        }
    };

  const handleViewInvoice = (order) => {
    setSelectedOrder(order);
    setShowInvoiceModal(true);
  };

  const handleCloseInvoiceModal = () => {
    setShowInvoiceModal(false);
    setSelectedOrder(null);
  };

    const handlePrint = () => {
        const printContents = document.getElementById('invoice-content').innerHTML;
        const originalContents = document.body.innerHTML;

        document.body.innerHTML = printContents;
        window.print();
        document.body.innerHTML = originalContents;

        window.location.reload(); // ✅ restore React safely
    };

  const getTicketLabel = (ticket) => {
    if (ticket.category?.name) return ticket.category.name;
    if (ticket.seat_map_seat_id) {
      return [ticket.section?.name, ticket.seat?.label].filter(Boolean).join(' · ') || 'Reserved Seat';
    }
    if (ticket.seat_map_section_id) {
      return ticket.section?.name ? ticket.section.name + ' (GA)' : 'General Admission';
    }
    return 'Ticket';
  };

  const getTicketPrice = (ticket) =>
    parseFloat(ticket.price || ticket.category?.price || 0).toFixed(2);

  const handleDownloadPDF = (order) => {
    const doc = new jsPDF();

    // Header
    doc.setFontSize(20);
    doc.setTextColor(250, 204, 21);
      doc.setFont('helvetica', 'bold');
    doc.text('TICKETATY', 19, 20);

    doc.setFontSize(16);
    doc.setTextColor(0, 0, 0);
    doc.text('INVOICE', 20, 35);

    // Order Info
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Order #: ${order.id}`, 20, 45);
    doc.text(`Date: ${new Date(order.created_at).toLocaleDateString()}`, 20, 50);
    doc.text(`Payment Status: ${order.payment_status}`, 20, 55);

    // Event Info
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text('Event Details', 20, 70);

    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Event: ${order.event?.name || 'N/A'}`, 20, 78);
    doc.text(`Location: ${order.event?.location || 'N/A'}`, 20, 84);
    doc.text(`Date: ${order.event?.start_date ? new Date(order.event.start_date).toLocaleDateString() : 'N/A'}`, 20, 90);

    // Tickets Table
    const currency = order.event?.currency || '';
    const tableData = order.tickets.map(ticket => [
      ticket.id,
      getTicketLabel(ticket),
      `${currency}${getTicketPrice(ticket)}`
    ]);
    
    autoTable(doc, {
      startY: 100,
      head: [['Ticket Number', 'Category', 'Price']],
      body: tableData,
      theme: 'grid',
      headStyles: { fillColor: [250, 204, 21] },
      margin: { left: 20, right: 20 }
    });
    
    // Totals
    const finalY = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text(`Subtotal:`, 130, finalY);
    doc.text(`${order.event.currency}${parseFloat(order.subtotal).toFixed(2)}`, 170, finalY, { align: 'right' });

    
    doc.text(`Discount:`, 130, finalY + 6);
    doc.text(`-${order.event.currency}${parseFloat(order.discount).toFixed(2)}`, 170, finalY + 6, { align: 'right' });
    
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text(`Total:`, 130, finalY + 15);
    doc.text(`${order.event.currency}${(
        parseFloat(order.subtotal || 0) -
        parseFloat(order.discount || 0)
    ).toFixed(2)}`, 170, finalY + 15, { align: 'right' });
    
    // Footer
    doc.setFontSize(8);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(150, 150, 150);
    doc.text('Thank you for your purchase!', 105, 280, { align: 'center' });
    doc.text('For any questions, contact support@ticketaty.com', 105, 285, { align: 'center' });
    
    // Save PDF
    doc.save(`invoice-${order.id}.pdf`);
    toast.success('Invoice downloaded successfully');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return 'badge-success';
      case 'pending':
        return 'bg-purple-200 text-purple-800';
      case 'confirmed':
        return 'badge-info';
      case 'failed':
        return 'badge-danger';
      case 'cancelled':
        return 'badge-secondary';
      default:
        return 'badge-secondary';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="container-fluid px-4 py-5">
        <div className="text-center py-5">
          <div className="spinner-border text-purple-500" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid px-4 py-5">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="h3 mb-0 text-gray-900 font-weight-bold">
          <i className="fas fa-shopping-cart mr-2"></i>My Orders
        </h1>
          <div className="mb-8 flex flex-col sm:flex-row gap-4">

              {/* Keyword search */}
              <div className="relative flex-1">
                  <input
                      type="text"
                      placeholder="Search event title"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-12 pr-4 py-3 !border !border-purple-500 rounded-full focus:ring-2 focus:ring-purple-500 outline-none"
                  />
                  <svg
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-purple-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                  >
                      <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M21 21l-4.35-4.35M17 10a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                  </svg>
              </div>

              {/* Date search */}
              <div className="relative">
                  <Flatpickr
                      value={selectedDate}
                      options={{
                          dateFormat: "Y-m-d",
                          allowInput: true,
                      }}
                      onChange={(dates, dateStr) => {
                          setSelectedDate(dateStr);
                      }}
                      placeholder="Select a date"
                      className="pl-12 pr-4 py-3 !border !border-purple-500 rounded-full outline-none focus:ring-2 focus:ring-purple-500"
                  />

                  <svg
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-purple-500 pointer-events-none"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                  >
                      <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                  </svg>

                  {selectedDate && (
                      <button
                          type="button"
                          onClick={() => setSelectedDate('')}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500"
                      >
                          ✕
                      </button>
                  )}
              </div>
          </div>

      </div>

      {orders.length === 0 ? (
        <div className="card shadow">
          <div className="card-body text-center py-5">
            <i className="fas fa-shopping-cart fa-3x text-muted mb-3"></i>
            <h5 className="text-muted">No orders found</h5>
            <p className="text-muted mb-4">You haven't made any purchases yet.</p>
            <a href="/events" className="btn !bg-purple-500 !font-bold text-white px-5 py-3 hover:!bg-purple-600">
              <i className="fas fa-search mr-2"></i>Browse Events
            </a>
          </div>
        </div>
      ) : (
        <div className="card shadow">
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Order #</th>
                    <th>Event</th>
                    <th>Date</th>
                    <th>Tickets</th>
                    <th>Total</th>
                    <th>Payment Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr key={order.id} style={{ cursor: 'pointer' }}>
                      <td onClick={() => handleViewInvoice(order)}>
                        <strong>{order.id}</strong>
                      </td>
                      <td onClick={() => handleViewInvoice(order)}>
                        <div className="d-flex align-items-center">
                          {order.event?.image ? (
                            <img
                              src={order.event.image}
                              alt={order.event.name}
                              className="rounded mr-2"
                              style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                            />
                          ) : (
                            <div
                              className="bg-purple-500 rounded mr-2 d-flex align-items-center justify-content-center"
                              style={{ width: '40px', height: '40px' }}
                            >
                              <i className="fas fa-calendar-alt text-white"></i>
                            </div>
                          )}
                          <div>
                            <div className="font-weight-bold">{order.event?.name}</div>
                            <small className="text-muted">{order.event?.location}</small>
                          </div>
                        </div>
                      </td>
                      <td onClick={() => handleViewInvoice(order)}>
                        <small>{formatDate(order.created_at)}</small>
                      </td>
                      <td onClick={() => handleViewInvoice(order)}>
                        <span className="badge bg-gray-200 text-black">
                          {order.tickets?.length || 0} tickets
                        </span>
                      </td>
                      <td onClick={() => handleViewInvoice(order)}>
                        <strong>{order.event?.currency}:{(
                            parseFloat(order.subtotal || 0) -
                            parseFloat(order.discount || 0)
                        ).toFixed(2)}</strong>
                      </td>
                      <td onClick={() => handleViewInvoice(order)}>
                        <span className={`badge ${getStatusBadge(order.payment_status)}`}>
                          {order.payment_status}
                        </span>
                      </td>
                      <td>
                          <button
                              onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewInvoice(order);
                              }}
                              className="group btn btn-sm rounded-lg !border !border-purple-500 !bg-transparent hover:!bg-purple-500 mr-1"
                              title="View Invoice"
                          >
                              <i className="fas fa-eye text-purple-500 group-hover:!text-black"></i>
                          </button>

                          <button
                              onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownloadPDF(order);
                              }}
                              className="group btn btn-sm rounded-lg !border !border-green-600 !bg-transparent hover:!bg-green-600"
                              title="Download PDF"
                          >
                              <i className="fas fa-file-pdf text-green-600 group-hover:!text-white"></i>
                          </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
                {renderPagination()}
            </div>
          </div>
        </div>
      )}

      {/* Invoice Modal */}
      {showInvoiceModal && selectedOrder && ReactDOM.createPortal(
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)', position: 'fixed', inset: 0, zIndex: 9999 }}
          onClick={handleCloseInvoiceModal}
        >
          <div
            className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable"
            style={{ maxWidth: '70vw' }}   // ✅ wider modal
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header bg-purple-500 text-white">
                <h5 className="modal-title text-white">
                  <i className="fas fa-file-invoice text-white mr-2"></i>

                </h5>
                <button
                  type="button"
                  className="close !text-white"
                  onClick={handleCloseInvoiceModal}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body" id="invoice-content">
                {/* Invoice Header */}
                <div className="row mb-4">
                  <div className="col-6">
                    <h2 className="text-purple-500 font-weight-bold">TICKETATY</h2>
                    <p className="text-muted mb-0">Your gateway to amazing events</p>
                  </div>
                  <div className="col-6 text-right">
                    <h3 className="text-black font-weight-bold">INVOICE</h3>
                    <p className="mb-0">
                      <strong>Order #:</strong> {selectedOrder.id}
                    </p>
                    <p className="mb-0">
                      <strong>Date:</strong> {formatDate(selectedOrder.created_at)}
                    </p>
                    <p className="mb-0">
                      <span className={`badge ${getStatusBadge(selectedOrder.payment_status)}`}>
                        {selectedOrder.payment_status}
                      </span>
                    </p>
                  </div>
                </div>

                <hr />

                {/* Event Details */}
                <div className="mb-4">
                  <h5 className="text-black font-weight-bold mb-3">Event Details</h5>
                  <div className="row">
                    <div className="col-md-3">
                      {selectedOrder.event?.image ? (
                        <img
                          src={selectedOrder.event.image}
                          alt={selectedOrder.event.name}
                          className="img-fluid rounded print-image"
                        />
                      ) : (
                        <div className="bg-secondary rounded d-flex align-items-center justify-content-center" style={{ height: '100px' }}>
                          <i className="fas fa-calendar-alt fa-2x text-white"></i>
                        </div>
                      )}
                    </div>
                    <div className="col-md-9">
                      <h6 className="font-weight-bold">{selectedOrder.event?.name}</h6>
                      <p className="mb-1">
                        <i className="fas fa-map-marker-alt mr-2 text-muted"></i>
                        {selectedOrder.event?.location}
                      </p>
                      <p className="mb-1">
                        <i className="fas fa-calendar mr-2 text-muted"></i>
                        {selectedOrder.event?.start_date ? formatDate(selectedOrder.event.start_date) : 'N/A'}
                      </p>
                      {selectedOrder.event?.organizer && (
                        <p className="mb-0">
                          <i className="fas fa-user mr-2 text-muted"></i>
                          Organized by {selectedOrder.event.organizer.name}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <hr />

                {/* Tickets */}
                <div className="mb-4">
                  <h5 className="font-weight-bold mb-3">Tickets</h5>
                  <div className="table-responsive">
                    <table className="table table-bordered">
                      <thead className="bg-light">
                        <tr>
                          <th>Ticket Number</th>
                          <th>Category</th>
                          <th>QR Code</th>
                          <th className="text-right">Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedOrder.tickets && selectedOrder.tickets.length > 0 ? (
                          selectedOrder.tickets.map((ticket) => (
                            <tr key={ticket.id}>
                              <td>
                                <small className="font-weight-bold">{ticket.id}</small>
                              </td>
                              <td>{getTicketLabel(ticket)}</td>
                              <td>
                                <span className="badge bg-gray-200 text-black">
                                  {ticket.qr_code || ticket.ticket_qr_code}
                                </span>
                              </td>
                              <td className="text-right">
                                {selectedOrder.event?.currency}{getTicketPrice(ticket)}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="4" className="text-center text-muted">
                              No tickets found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                <hr />

                {/* Totals */}
                <div className="row">
                  <div className="col-md-6 offset-md-6">
                    <table className="table table-sm">
                      <tbody>
                        <tr>
                          <td>Subtotal:</td>
                          <td className="text-right">{selectedOrder.event.currency}{parseFloat(selectedOrder.subtotal).toFixed(2)}</td>
                        </tr>
                        {/*<tr>*/}
                        {/*  <td>Tax:</td>*/}
                        {/*  <td className="text-right">${parseFloat(selectedOrder.tax).toFixed(2)}</td>*/}
                        {/*</tr>*/}
                        <tr>
                          <td>Discount:</td>
                          <td className="text-right">-{selectedOrder.event.currency}{parseFloat(selectedOrder.discount).toFixed(2)}</td>
                        </tr>
                        <tr className="font-weight-bold border-top">
                          <td><h5 className="mb-0">Total:</h5></td>
                          <td className="text-right"><h5 className="mb-0 text-black">{selectedOrder.event.currency}{(
                              parseFloat(selectedOrder.subtotal || 0) -
                              parseFloat(selectedOrder.discount || 0)
                          ).toFixed(2)}</h5></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <hr />

                {/* Footer */}
                <div className="text-center text-muted">
                  <p className="mb-1">Thank you for your purchase!</p>
                  <p className="mb-0">
                    <small>For any questions, contact support@ticketaty.com</small>
                  </p>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn !bg-green-600 !text-white"
                  onClick={() => handleDownloadPDF(selectedOrder)}
                >
                  <i className="fas fa-download mr-2"></i>
                  Download PDF
                </button>
                <button
                  type="button"
                  className="btn !bg-purple-500 !text-white"
                  onClick={handlePrint}
                >
                  <i className="fas fa-print mr-2"></i>
                  Print
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default MyOrders;

